import {
  FC,
  useRef,
  MutableRefObject,
  MouseEvent,
  TouchEvent
} from 'react';

import { TinyColor } from '../../../utils';
import { useDragListeners } from '../../../utils/useDragListeners';
import { TPropsComp, TCoords } from './types';

const WIDTH = 200;
const HEIGHT = 150;

const Board: FC<TPropsComp> = ({
  color,
  colorBoardHeight,
  onChange,
  setChange
}) => {
  const node = useRef() as MutableRefObject<HTMLDivElement>;
  const drag = useDragListeners();

  const removeListeners = () => {
    setChange(false);
    drag.stop();
  };

  const removeTouchListeners = () => {
    setChange(false);
    drag.stop();
  };

  const onBoardMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    const buttons = e.buttons;

    if (buttons !== 1) return;

    removeListeners();

    const x = e.clientX;
    const y = e.clientY;
    pointMoveTo({ x, y });

    drag.start({ mousemove: onBoardDrag, mouseup: onBoardDragEnd });
  };

  const onBoardTouchStart = (e: TouchEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    if (e.touches.length !== 1) {
      return;
    }

    removeTouchListeners();

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;

    pointMoveTo({ x, y });

    drag.start(
      { touchmove: onBoardTouchMove, touchend: onBoardTouchEnd },
      { passive: false }
    );
  };

  const onBoardTouchMove = (e: any) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;

    pointMoveTo({
      x,
      y
    });
  };

  const onBoardTouchEnd = () => {
    removeTouchListeners();
  };

  const onBoardDrag = (e: any) => {
    e.preventDefault();
    const x = e.clientX;
    const y = e.clientY;

    pointMoveTo({
      x,
      y
    });
  };

  const onBoardDragEnd = (e: any) => {
    e.preventDefault();
    const x = e.clientX;
    const y = e.clientY;

    pointMoveTo({
      x,
      y
    });
    removeListeners();
  };

  const pointMoveTo = (pos: TCoords) => {
    const rect = node.current?.getBoundingClientRect();
    if (!rect) return;

    let left = pos.x - rect.left;
    let top = pos.y - rect.top;

    const rWidth = rect.width || WIDTH;
    const rHeight = rect.height || HEIGHT;

    left = Math.max(0, left);
    left = Math.min(left, rWidth);
    top = Math.max(0, top);
    top = Math.min(top, rHeight);

    color.saturation = left / rWidth;
    color.brightness = 1 - top / rHeight;

    onChange(color);
  };

  const hueHsv = {
    h: color.hue,
    s: 1,
    v: 1
  };

  const hueColor = new TinyColor(hueHsv).toHexString();

  const xRel = color.saturation * 100;
  const yRel = (1 - color.brightness) * 100;

  return (
    <div className='relative mb-4 select-none text-[0px]' ref={node}>
      <div
        className='relative z-[1] w-full rounded-md'
        style={{
          backgroundColor: hueColor,
          height: `${colorBoardHeight}px`,
          minHeight: `${colorBoardHeight}px`
        }}
      >
        {/* brilho (preto por cima) e saturação (branco pela esquerda) */}
        <div className='absolute inset-0 z-[2] rounded-md bg-[linear-gradient(to_bottom,transparent_0%,#000_100%)]' />
        <div className='absolute inset-0 z-[1] rounded-md bg-[linear-gradient(to_right,#fff_0%,transparent_100%)]' />
      </div>

      <span
        className='absolute z-[2] size-[14px] rounded-full border-2 border-white shadow-[0_2px_4px_0_rgba(0,0,0,0.24)]'
        style={{
          left: `calc(${xRel}% - 7px)`,
          top: `calc(${yRel}% - 7px)`,
          backgroundColor: color.toHexString()
        }}
      />

      <div
        className='absolute inset-0 z-[3] cursor-grab select-none'
        onMouseDown={onBoardMouseDown}
        onTouchStart={onBoardTouchStart}
      />
    </div>
  );
};

export default Board;
